/**
 * Lighting maths in plain numbers, mirroring the GLSL in shaders.ts (LIGHTING block) line for line. The shaders are
 * the renderer; this file exists so the same formulas can be unit-tested and so the uniforms are derived in one place.
 *
 * Prod has two lighting models (docs/research/lighting-parity.md):
 *  - impostors (atoms, bonds): ImpostorCommon.hlsl:138-151, albedo * (Lambert + SampleSH) + Blinn-Phong specular;
 *  - ribbons and surfaces: URP Lit shader graphs (Lit_VertexColor.shadergraph, Lit_Texture*.shadergraph), i.e.
 *    UniversalFragmentPBR in URP 17.3.0 (Packages/manifest.json:28): Lighting.hlsl LightingPhysicallyBased and
 *    BRDF.hlsl InitializeBRDFData / DirectBRDFSpecular, plus GlobalIllumination with occlusion 1.
 * Both use the one directional light and the flat ambient colour; HDR is off and nothing tonemaps
 * (Universal Render Pipeline Asset.asset:26, :80), so values above 1 clip in prod exactly as they do here.
 */
import { LIGHTING } from '@nanome-lite/look';

export type Vec3 = readonly [number, number, number];

const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const saturate = (x: number): number => Math.min(1, Math.max(0, x));
function normalize(a: Vec3): Vec3 {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
}

/** URP BRDF.hlsl constants: HALF_MIN_SQRT and HALF_MIN clamp the roughness so the GGX term stays finite. */
const HALF_MIN_SQRT = 0.0078125;
const HALF_MIN = 6.103515625e-5;

export interface UrpBrdf {
  /** roughness^2, with roughness = (1 - smoothness)^2 */
  roughness2: number;
  roughness2MinusOne: number;
  /** roughness * 4 + 2 */
  normalizationTerm: number;
  /** 1 - reflectivity: the diffuse colour is albedo times this (0.96 at metallic 0) */
  oneMinusReflectivity: number;
  /** specular colour F0 (0.04 at metallic 0) */
  dielectricSpec: number;
}

/** BRDF.hlsl InitializeBRDFData for metallic 0. */
export function urpLitBrdf(smoothness: number, dielectricSpec: number = LIGHTING.urpDielectricSpec): UrpBrdf {
  const perceptualRoughness = 1 - smoothness;
  const roughness = Math.max(perceptualRoughness * perceptualRoughness, HALF_MIN_SQRT);
  const roughness2 = Math.max(roughness * roughness, HALF_MIN);
  return { roughness2, roughness2MinusOne: roughness2 - 1, normalizationTerm: roughness * 4 + 2, oneMinusReflectivity: 1 - dielectricSpec, dielectricSpec };
}

/** BRDF.hlsl DirectBRDFSpecular: the GGX term URP multiplies by the specular colour. */
export function urpSpecularTerm(brdf: UrpBrdf, n: Vec3, l: Vec3, v: Vec3): number {
  const h = normalize([l[0] + v[0], l[1] + v[1], l[2] + v[2]]);
  const NoH = saturate(dot(n, h)), LoH = saturate(dot(l, h));
  const d = NoH * NoH * brdf.roughness2MinusOne + 1.00001;
  return brdf.roughness2 / (d * d * Math.max(0.1, LoH * LoH) * brdf.normalizationTerm);
}

export interface LightRig {
  /** unit vector toward the light, same space as the normal */
  toLight: Vec3;
  /** light colour times intensity, linear */
  lightColor: number;
  /** flat ambient, linear */
  ambient: number;
  /** main-light shadow attenuation, 1 = lit */
  shadow?: number;
}

/**
 * One linear colour channel of a URP Lit surface (GLSL litLighting): (diffuse + F0 * specularTerm) * radiance
 * + diffuse * ambient, with radiance = light * shadow * N.L. Not clamped.
 */
export function shadeUrpLit(albedo: number, n: Vec3, v: Vec3, rig: LightRig, brdf: UrpBrdf): number {
  const radiance = rig.lightColor * (rig.shadow ?? 1) * saturate(dot(n, rig.toLight));
  const diffuse = albedo * brdf.oneMinusReflectivity;
  return (diffuse + brdf.dielectricSpec * urpSpecularTerm(brdf, n, rig.toLight, v)) * radiance + diffuse * rig.ambient;
}

/**
 * One linear colour channel of an impostor (GLSL impostorLighting, ImpostorCommon.hlsl:146-150):
 * albedo * (Lambert + ambient) + light * pow(N.H, exponent) * specular colour. Not clamped.
 */
export function shadeImpostor(albedo: number, n: Vec3, v: Vec3, rig: LightRig, specularExponent: number = LIGHTING.impostorSpecularExponent, specularColor: number = LIGHTING.impostorSpecularColor): number {
  const light = rig.lightColor * (rig.shadow ?? 1);
  const h = normalize([rig.toLight[0] + v[0], rig.toLight[1] + v[1], rig.toLight[2] + v[2]]);
  const diffuse = light * saturate(dot(n, rig.toLight));
  const specular = light * Math.pow(saturate(dot(n, h)), specularExponent) * specularColor;
  return albedo * (diffuse + rig.ambient) + specular;
}

/** Exact sRGB encode of the resolve blit (COMPOSITE_FRAG), on a value already clamped to [0, 1]. */
export function linearToSrgb(c: number): number {
  const x = saturate(c);
  return x < 0.0031308 ? x * 12.92 : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
}
